import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  KeyRound,
  Download,
  RefreshCw,
  Search,
  LogOut,
  CheckCircle2,
  XCircle,
  Database,
  ArrowRight,
  Mail,
  Activity,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "shescan.admin.key";

export default function Admin() {
  const [adminKey, setAdminKey] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || ""
  );
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Dashboard state
  const [signups, setSignups] = useState([]);
  const [brands, setBrands] = useState([]);
  const [airtable, setAirtable] = useState(null);
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [shopFilter, setShopFilter] = useState("ALL");
  const [tab, setTab] = useState("shoppers"); // 'shoppers' | 'brands'

  useEffect(() => {
    // Attempt auto-auth if stored key is present
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) tryAuth(stored, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed) {
      loadSignups();
      loadBrands();
      checkAirtable();
      loadStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const refreshAll = () => {
    loadSignups();
    loadBrands();
    checkAirtable();
    loadStatus();
    toast.info("Refreshed");
  };


  const tryAuth = async (key, opts = {}) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/auth`, { key });
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthed(true);
      setAdminKey(key);
      if (!opts.silent) toast.success("Admin access granted");
    } catch (err) {
      sessionStorage.removeItem(STORAGE_KEY);
      if (!opts.silent)
        toast.error("Invalid admin key", {
          description: err?.response?.data?.detail || "Check the key and try again.",
        });
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setAdminKey("");
    setSignups([]);
    setBrands([]);
    setAirtable(null);
    setStatus(null);
  };

  const loadSignups = async () => {
    try {
      const res = await axios.get(`${API}/beta/signups`, {
        headers: { "X-Admin-Key": sessionStorage.getItem(STORAGE_KEY) },
      });
      setSignups(res.data || []);
    } catch (err) {
      toast.error("Failed to load signups", {
        description: err?.response?.data?.detail,
      });
    }
  };

  const loadBrands = async () => {
    try {
      const res = await axios.get(`${API}/brand/registrations`, {
        headers: { "X-Admin-Key": sessionStorage.getItem(STORAGE_KEY) },
      });
      setBrands(res.data || []);
    } catch (err) {
      // Non-fatal — brand endpoint may not exist yet in older deploys
      setBrands([]);
    }
  };

  const checkAirtable = async () => {
    try {
      const res = await axios.get(`${API}/airtable/verify`, {
        headers: { "X-Admin-Key": sessionStorage.getItem(STORAGE_KEY) },
      });
      setAirtable(res.data);
    } catch {
      setAirtable({ ok: false, status: "error", detail: "Unable to verify." });
    }
  };

  const loadStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/status`, {
        headers: { "X-Admin-Key": sessionStorage.getItem(STORAGE_KEY) },
      });
      setStatus(res.data);
    } catch {
      setStatus(null);
    }
  };

  const downloadCSV = async () => {
    const path = tab === "brands" ? "/brand/export.csv" : "/beta/export.csv";
    const label = tab === "brands" ? "brands" : "applicants";
    try {
      const res = await axios.get(`${API}${path}`, {
        headers: { "X-Admin-Key": sessionStorage.getItem(STORAGE_KEY) },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      a.download = `shescan_${label}_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (err) {
      toast.error("Export failed", {
        description: err?.response?.data?.detail || "",
      });
    }
  };

  // Derived lists
  const shops = useMemo(() => {
    const s = new Set(signups.map((r) => r.shop).filter(Boolean));
    return ["ALL", ...Array.from(s).sort()];
  }, [signups]);

  const filteredSignups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return signups.filter((r) => {
      if (shopFilter !== "ALL" && r.shop !== shopFilter) return false;
      if (!q) return true;
      return (
        (r.first_name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.frustration || "").toLowerCase().includes(q)
      );
    });
  }, [signups, query, shopFilter]);

  const filteredBrands = useMemo(() => {
    const q = query.trim().toLowerCase();
    return brands.filter((r) => {
      if (!q) return true;
      return (
        (r.brand_name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.website || "").toLowerCase().includes(q) ||
        (r.category || "").toLowerCase().includes(q)
      );
    });
  }, [brands, query]);

  const isBrandTab = tab === "brands";
  const filteredCount = isBrandTab ? filteredBrands.length : filteredSignups.length;

  if (!authed) {
    return <AdminLogin loading={loading} onSubmit={tryAuth} />;
  }

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-paper text-ink font-sans"
    >
      {/* Header */}
      <header className="border-b border-ink bg-paper sticky top-0 z-30 backdrop-blur-xl">
        <div className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="font-display font-black uppercase tracking-tight text-ink text-lg"
            >
              SheScan
            </a>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.28em] text-ink-soft">
              / Admin console
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={refreshAll}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-ink hover:text-paper transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-ink bg-ink text-paper px-3 py-2 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-brand-orange hover:border-brand-orange transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Lock
            </button>
          </div>
        </div>
      </header>

      <main className="w-full max-w-none px-4 sm:px-8 lg:px-12 py-10">
        {/* System status panel */}
        {status && <SystemStatus status={status} />}

        {/* Tab switcher */}
        <div className="flex gap-0 border border-ink mb-6" role="tablist">
          <button
            role="tab"
            aria-selected={!isBrandTab}
            onClick={() => setTab("shoppers")}
            data-testid="admin-tab-shoppers"
            className={`font-mono text-[11px] uppercase tracking-[0.22em] px-6 py-3 border-r border-ink transition-colors ${
              !isBrandTab ? "bg-ink text-paper" : "bg-paper text-ink hover:bg-paper-soft"
            }`}
          >
            Shoppers ({signups.length})
          </button>
          <button
            role="tab"
            aria-selected={isBrandTab}
            onClick={() => setTab("brands")}
            data-testid="admin-tab-brands"
            className={`font-mono text-[11px] uppercase tracking-[0.22em] px-6 py-3 transition-colors ${
              isBrandTab ? "bg-brand-orange text-white" : "bg-paper text-ink hover:bg-paper-soft"
            }`}
          >
            Brands ({brands.length})
          </button>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-ink mb-10">
          {isBrandTab ? (
            <>
              <Stat
                label="Total brand applications"
                value={brands.length}
                testid="stat-total"
              />
              <Stat
                label="Unique categories"
                value={new Set(brands.map((r) => r.category)).size}
                border
                testid="stat-shops"
              />
            </>
          ) : (
            <>
              <Stat
                label="Total applicants"
                value={signups.length}
                testid="stat-total"
              />
              <Stat
                label="Unique shops"
                value={new Set(signups.map((r) => r.shop)).size}
                border
                testid="stat-shops"
              />
            </>
          )}
          <AirtableStatus airtable={airtable} />
        </div>

        {/* Filter bar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto md:flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  isBrandTab
                    ? "Search brand, email, website, category..."
                    : "Search name, email, notes..."
                }
                data-testid="admin-search"
                className="w-full bg-paper border border-ink pl-10 pr-4 py-2.5 font-mono text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-brand-orange"
              />
            </div>
            {!isBrandTab && (
              <select
                value={shopFilter}
                onChange={(e) => setShopFilter(e.target.value)}
                data-testid="admin-shop-filter"
                className="bg-paper border border-ink px-4 py-2.5 font-mono text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand-orange rounded-none"
              >
                {shops.map((s) => (
                  <option key={s} value={s}>
                    {s === "ALL" ? "All shops" : s}
                  </option>
                ))}
              </select>
            )}
          </div>
          <button
            onClick={downloadCSV}
            data-testid="admin-export-csv"
            className="inline-flex items-center gap-2 border border-ink bg-ink text-paper px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-brand-orange hover:border-brand-orange transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV ({filteredCount})
          </button>
        </div>

        {/* Table */}
        <div className="border border-ink overflow-x-auto">
          {isBrandTab ? (
            <BrandsTable rows={filteredBrands} totalCount={brands.length} />
          ) : (
            <ShoppersTable rows={filteredSignups} totalCount={signups.length} />
          )}
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
          Source of truth · MongoDB · synced to Airtable{" "}
          <span className="text-brand-orange">Applicants</span>
        </p>
      </main>
    </div>
  );
}

function SystemStatus({ status }) {
  const rows = status?.rows || [];
  const allOk = status?.all_ok;
  return (
    <div
      data-testid="admin-system-status"
      className="mb-8 border border-ink bg-paper"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-ink">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5 text-brand-orange" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink">
            System status
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="admin-status-badge"
            className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] ${
              allOk ? "text-ink" : "text-brand-red"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                allOk ? "bg-brand-orange" : "bg-brand-red"
              }`}
              aria-hidden
            />
            {allOk ? "All systems operational" : "Degraded"}
          </span>
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
            {formatDate(status?.checked_at)}
          </span>
        </div>
      </div>
      <ul className="divide-y divide-ink/10">
        {rows.map((r) => (
          <li
            key={r.name}
            data-testid={`admin-status-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="flex items-start gap-4 px-5 py-3"
          >
            <div className="mt-0.5 shrink-0">
              {r.ok ? (
                <CheckCircle2 className="w-4 h-4 text-brand-orange" />
              ) : (
                <XCircle className="w-4 h-4 text-brand-red" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-sans font-semibold text-sm text-ink">
                {r.name}
              </div>
              <div className="font-mono text-[11px] text-ink-soft mt-0.5 break-words">
                {r.detail}
              </div>
            </div>
          </li>
        ))}
      </ul>
      {status?.counts && (
        <div className="grid grid-cols-3 border-t border-ink text-center">
          <StatusCount label="Shoppers" value={status.counts.shoppers} />
          <StatusCount label="Brands" value={status.counts.brands} border />
          <StatusCount label="Extension" value={status.counts.extension_waitlist} />
        </div>
      )}
    </div>
  );
}

function StatusCount({ label, value, border }) {
  return (
    <div
      className={`py-3 px-3 ${border ? "border-l border-r border-ink" : ""}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
        {label}
      </div>
      <div className="font-mono text-2xl text-ink mt-1 tabular-nums">
        {typeof value === "number" ? value : "—"}
      </div>
    </div>
  );
}

function Stat({ label, value, border, testid }) {
  return (
    <div
      data-testid={testid}
      className={`p-6 ${border ? "md:border-l md:border-r border-ink" : ""}`}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft">
        {label}
      </div>
      <div className="font-mono tracking-tighter text-5xl text-ink mt-2">
        {value}
      </div>
    </div>
  );
}

function ShoppersTable({ rows, totalCount }) {
  return (
    <table data-testid="admin-table" className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-ink text-paper">
          {["#", "First name", "Email", "Shop", "Notes", "Created", "Action"].map((h) => (
            <th
              key={h}
              className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] border-r border-paper/20 last:border-r-0"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={7} className="px-4 py-16 text-center text-ink-soft font-mono text-sm">
              {totalCount === 0
                ? "No applicants yet. The beta cohort is in formation."
                : "No results for the current filter."}
            </td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr
              key={r.id}
              data-testid={`admin-row-${i}`}
              className="border-t border-ink hover:bg-paper-soft transition-colors"
            >
              <td className="px-4 py-3 font-mono text-[11px] text-ink-soft">
                {String(i + 1).padStart(3, "0")}
              </td>
              <td className="px-4 py-3 font-medium text-ink">{r.first_name}</td>
              <td className="px-4 py-3 font-mono text-xs text-ink">
                <a href={`mailto:${r.email}`} className="link-under">
                  {r.email}
                </a>
              </td>
              <td className="px-4 py-3 text-ink-soft text-xs">{r.shop}</td>
              <td className="px-4 py-3 text-ink-soft text-xs max-w-sm truncate">
                {r.frustration || "—"}
              </td>
              <td className="px-4 py-3 font-mono text-[11px] text-ink-soft whitespace-nowrap">
                {formatDate(r.created_at)}
              </td>
              <td className="px-4 py-3 whitespace-nowrap">
                <a
                  href={buildInviteMailto(r)}
                  data-testid={`admin-invite-${i}`}
                  className="inline-flex items-center gap-2 border border-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-ink hover:bg-brand-orange hover:border-brand-orange hover:text-paper transition-colors"
                >
                  <Mail className="w-3 h-3" /> Invite
                </a>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function BrandsTable({ rows, totalCount }) {
  return (
    <table data-testid="admin-brands-table" className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-brand-orange text-white">
          {["#", "Brand", "Email", "Website", "Category", "Created"].map((h) => (
            <th
              key={h}
              className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] border-r border-white/20 last:border-r-0"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={6} className="px-4 py-16 text-center text-ink-soft font-mono text-sm">
              {totalCount === 0
                ? "No brand applications yet. The registry is in formation."
                : "No results for the current search."}
            </td>
          </tr>
        ) : (
          rows.map((r, i) => (
            <tr
              key={r.id}
              data-testid={`admin-brand-row-${i}`}
              className="border-t border-ink hover:bg-paper-soft transition-colors"
            >
              <td className="px-4 py-3 font-mono text-[11px] text-ink-soft">
                {String(i + 1).padStart(3, "0")}
              </td>
              <td className="px-4 py-3 font-medium text-ink">{r.brand_name}</td>
              <td className="px-4 py-3 font-mono text-xs text-ink">
                <a href={`mailto:${r.email}`} className="link-under">
                  {r.email}
                </a>
              </td>
              <td className="px-4 py-3 font-mono text-xs text-ink">
                <a
                  href={r.website?.startsWith("http") ? r.website : `https://${r.website}`}
                  target="_blank"
                  rel="noreferrer"
                  className="link-under"
                >
                  {r.website}
                </a>
              </td>
              <td className="px-4 py-3 text-ink-soft text-xs">{r.category}</td>
              <td className="px-4 py-3 font-mono text-[11px] text-ink-soft whitespace-nowrap">
                {formatDate(r.created_at)}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}


function AirtableStatus({ airtable }) {
  const ok = airtable?.ok;
  return (
    <div data-testid="stat-airtable" className="p-6">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-soft flex items-center gap-2">
        <Database className="w-3 h-3" /> Airtable · Applicants
      </div>
      <div
        className={`font-mono tracking-tight text-xl mt-2 flex items-center gap-2 ${
          ok ? "text-ink" : "text-brand-red"
        }`}
      >
        {ok ? (
          <>
            <CheckCircle2 className="w-5 h-5 text-brand-orange" /> Connected
          </>
        ) : (
          <>
            <XCircle className="w-5 h-5" /> {airtable?.status || "Checking..."}
          </>
        )}
      </div>
      {!ok && airtable?.detail && (
        <p className="mt-2 text-[11px] font-mono text-ink-soft break-words">
          {typeof airtable.detail === "string"
            ? airtable.detail
            : airtable.detail?.error?.message || "See logs."}
        </p>
      )}
    </div>
  );
}

function AdminLogin({ loading, onSubmit }) {
  const [key, setKey] = useState("");
  const submit = (e) => {
    e.preventDefault();
    onSubmit(key.trim());
  };
  return (
    <div
      data-testid="admin-login"
      className="min-h-screen bg-ink text-paper flex items-center justify-center p-6"
    >
      <div className="w-full max-w-md border border-paper/30 bg-[#072a1b]">
        <div className="px-6 py-3 border-b border-paper/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5 text-brand-orange" />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/80">
              SheScan · Admin access
            </span>
          </div>
          <a
            href="/"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/60 hover:text-brand-orange"
          >
            ← Home
          </a>
        </div>

        <form onSubmit={submit} className="p-8 space-y-6">
          <div>
            <h1 className="font-display font-black uppercase text-4xl leading-[0.92] text-paper">
              Applicant ledger.
            </h1>
            <p className="mt-3 text-sm text-paper/70 leading-relaxed">
              Gated by admin key. Everyone else sees the marketing site.
            </p>
          </div>

          <div className="space-y-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/70 block">
              Admin key
            </label>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Paste admin key"
              autoFocus
              data-testid="admin-key-input"
              className="w-full bg-transparent border border-paper/30 px-4 py-4 font-mono text-sm text-paper placeholder:text-paper/40 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !key.trim()}
            data-testid="admin-login-submit"
            className="group w-full inline-flex items-center justify-center gap-3 border border-brand-orange bg-brand-orange text-paper font-mono uppercase text-xs tracking-[0.22em] px-6 py-4 hover:bg-paper hover:text-ink hover:border-paper transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Verifying..." : "Unlock console"}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function buildInviteMailto(r) {
  const to = r.email || "";
  const name = r.first_name || "there";
  const frustration = (r.frustration || "").trim();
  const subject = `SheScan beta — you're in, ${name}`;
  const lines = [
    `Hi ${name},`,
    ``,
    `Thanks for putting your name down for the SheScan beta cohort. You're in.`,
    ``,
    frustration
      ? `You told us the thing that frustrates you most when trying to buy women-owned is: "${frustration}" — that's exactly the problem we're building this product to close.`
      : `We're building SheScan to close the exact problem you've been running into: ownership is invisible at the point of purchase.`,
    ``,
    `Do you have 20 minutes this week for a short call? I'd like to walk you through where the product is, show you what we're testing next, and get your input on one or two features before we lock them in.`,
    ``,
    `Reply with a couple of windows that work and I'll send a calendar invite.`,
    ``,
    `— The SheScan team`,
    `hello@shescan.app`,
  ];
  const body = lines.join("\n");
  const params = new URLSearchParams({ subject, body });
  // URLSearchParams encodes spaces as '+', mailto clients prefer %20 — swap it.
  const qs = params.toString().replace(/\+/g, "%20");
  return `mailto:${encodeURIComponent(to)}?${qs}`;
}
